/**
 * TableForm Component
 * Step 1: Pool table information form
 */

import { PoolTable3DViewer } from './PoolTable3DViewer.js';

export class TableForm {
  constructor(containerId, initialData = {}, onValidate, onDataChange, roomData = {}) {
    this.container = document.getElementById(containerId);
    this.data = { ...initialData };
    this.roomData = roomData; // Room data for projector positioning
    this.onValidate = onValidate;
    this.onDataChange = onDataChange;
    this.errors = {};
    this.touchedFields = {}; // Track which fields have been interacted with
    this.viewer = null;
    this.viewerExpanded = true; // Track whether 3D preview is expanded (shown by default)
    this.render();
  }

  render() {
    // Destroy existing viewer before re-rendering to prevent memory leaks
    if (this.viewer) {
      this.viewer.destroy();
      this.viewer = null;
    }
    
    this.container.innerHTML = `
      <div class="form-container">
        <h2 class="form-title">Pool Table Information</h2>
        <p class="form-description">Enter your pool table size and height.</p>
        
        <div class="form-grid form-grid-compact">
          <div class="form-group form-group-side-by-side">
            <label for="table-size" class="form-label">
              Table Size <span class="required">*</span>
            </label>
            <select
              id="table-size"
              class="form-select ${this.errors.size ? 'error' : ''}"
              required
            >
              <option value="">Select table size</option>
              <option value="7" ${this.data.size === '7' ? 'selected' : ''}>Seven Foot</option>
              <option value="8" ${this.data.size === '8' ? 'selected' : ''}>Eight Foot</option>
              <option value="9" ${this.data.size === '9' ? 'selected' : ''}>Nine Foot</option>
            </select>
            ${this.errors.size ? `<div class="form-error">${this.errors.size}</div>` : ''}
            <p class="form-hint">Standard pool table sizes (felt playing surface length)</p>
          </div>

          <div class="form-group form-group-side-by-side">
            <label for="table-height" class="form-label">
              Table Height <span class="required">*</span>
            </label>
            <select
              id="table-height"
              class="form-select ${this.errors.height ? 'error' : ''}"
              required
            >
              <option value="">Select table height</option>
              ${Array.from({ length: 13 }, (_, i) => {
                const height = 26 + i;
                const selected = this.data.heightInches === height || (this.data.height && !isNaN(this.data.height) && Math.round(this.data.height * 12) === height);
                return `<option value="${height}" ${selected ? 'selected' : ''}>${height}"</option>`;
              }).join('')}
            </select>
            ${this.errors.height ? `<div class="form-error">${this.errors.height}</div>` : ''}
            <p class="form-hint">Standard height is typically 30-36 inches (2.5-3 feet) from floor</p>
          </div>
        </div>
        
        <div class="viewer-toggle-section" style="margin-top: 2rem;">
          <button class="viewer-toggle-btn" id="table-viewer-toggle" type="button">
            <span class="viewer-toggle-text">${this.viewerExpanded ? 'Hide 3D Preview' : 'Show 3D Preview'}</span>
            <span class="viewer-toggle-icon">${this.viewerExpanded ? '▲' : '▼'}</span>
          </button>
          <div class="viewer-toggle-content" id="table-viewer-content" style="display: ${this.viewerExpanded ? 'block' : 'none'};">
            <div id="table-3d-viewer-container"></div>
          </div>
        </div>
      </div>
    `;

    // Add event listener for table size dropdown to auto-calculate dimensions
    const tableSizeSelect = this.container.querySelector('#table-size');
    if (tableSizeSelect) {
      tableSizeSelect.addEventListener('change', () => {
        this.touchedFields.size = true;
        const size = parseFloat(tableSizeSelect.value);
        if (size) {
          // Update data and trigger validation
          this.handleInputChange();
          this.validate();
        }
      });
    }

    // Setup dropdown for table height
    const heightSelect = this.container.querySelector('#table-height');
    if (heightSelect) {
      heightSelect.addEventListener('change', () => {
        this.touchedFields.height = true;
        this.handleInputChange();
        this.validate();
      });
    }
    
    // Setup viewer toggle
    const viewerToggle = this.container.querySelector('#table-viewer-toggle');
    const viewerContent = this.container.querySelector('#table-viewer-content');
    if (viewerToggle && viewerContent) {
      viewerToggle.addEventListener('click', () => {
        this.viewerExpanded = !this.viewerExpanded;
        viewerContent.style.display = this.viewerExpanded ? 'block' : 'none';
        const toggleText = viewerToggle.querySelector('.viewer-toggle-text');
        const toggleIcon = viewerToggle.querySelector('.viewer-toggle-icon');
        if (toggleText) {
          toggleText.textContent = this.viewerExpanded ? 'Hide 3D Preview' : 'Show 3D Preview';
        }
        if (toggleIcon) {
          toggleIcon.textContent = this.viewerExpanded ? '▲' : '▼';
        }
        
        // Initialize viewer if it's being shown for the first time
        if (this.viewerExpanded && !this.viewer) {
          this.initViewer();
        }
      });
    }
    
    // Initialize viewer if it's already expanded (e.g., after re-render)
    if (this.viewerExpanded && !this.viewer) {
      this.initViewer();
    }
  }
  
  initViewer() {
    const viewerContainer = this.container.querySelector('#table-3d-viewer-container');
    if (!viewerContainer) {
      console.warn('Viewer container not found');
      return;
    }
    
    if (this.viewer) {
      // Viewer already exists, just update it
      this.updateViewer();
      return;
    }
    
    try {
      // Create new viewer
      this.viewer = new PoolTable3DViewer(viewerContainer, {
        width: 800,
        height: 400
      });
      // Update after a small delay to ensure Three.js is initialized
      setTimeout(() => {
        if (this.viewer) {
          this.updateViewer();
        }
      }, 100);
    } catch (error) {
      console.error('Error initializing 3D viewer:', error);
      // Viewer will show its own error message
    }
  }
  
  updateViewer() {
    if (!this.viewer) {
      // If viewer doesn't exist, try to initialize it
      this.initViewer();
      return;
    }
    
    try {
      const params = {
        tableLength: this.data.length,
        tableWidth: this.data.width,
        tableHeightInches: this.data.heightInches,
        ceilingHeightInches: this.roomData.ceilingHeight || null,
        mountingOption: this.roomData.mountingOption || null,
      };
      
      this.viewer.updateParams(params);
    } catch (error) {
      console.error('Error updating viewer:', error);
    }
  }
  
  setRoomData(roomData) {
    this.roomData = roomData;
    this.updateViewer();
  }

  handleInputChange() {
    const sizeSelect = this.container.querySelector('#table-size');
    const size = sizeSelect ? sizeSelect.value : null;
    
    // Calculate length and width from size
    let length = null;
    let width = null;
    if (size) {
      const sizeNum = parseFloat(size);
      length = sizeNum;
      width = sizeNum / 2;
    }
    
    // Get height in inches and convert to feet for backend
    const heightSelect = this.container.querySelector('#table-height');
    const heightInches = heightSelect ? parseFloat(heightSelect.value) : null;
    const height = heightInches ? heightInches / 12.0 : null;
    
    this.data = {
      size: size || null,
      length: length,
      width: width,
      height: height, // in feet for backend
      heightInches: heightInches, // in inches for frontend
    };

    if (this.onDataChange) {
      this.onDataChange(this.data);
    }
    
    // Update viewer when data changes
    this.updateViewer();
  }

  validate(validateAllFields = false) {
    this.errors = {};

    const size = this.data.size;
    const heightInches = this.data.heightInches;

    // Check if all required fields have been touched
    const allFieldsTouched = this.touchedFields.size && this.touchedFields.height;
    
    // Determine if we should validate all fields (either explicitly requested, or all fields have been touched)
    const shouldValidateAll = validateAllFields || allFieldsTouched;

    // Only show errors for touched fields (for display purposes)
    // But validate all fields if they've all been touched (for button state)
    if (shouldValidateAll || this.touchedFields.size || size) {
      if (!size) {
        this.errors.size = 'Please select a table size';
      } else if (!['7', '8', '9'].includes(size)) {
        this.errors.size = 'Table size must be 7, 8, or 9 feet';
      }
    }

    if (shouldValidateAll || this.touchedFields.height || heightInches) {
      if (!heightInches || heightInches <= 0) {
        this.errors.height = 'Table height is required and must be greater than 0';
      } else if (heightInches < 26 || heightInches > 38) {
        this.errors.height = 'Table height must be between 26 and 38 inches';
      }
    }

    this.render();
    
    // Update viewer after render if it exists
    if (this.viewer) {
      this.updateViewer();
    }

    // For button state: form is valid only if all required fields have been touched AND are valid
    // If not all fields have been touched, form is not valid yet
    const hasErrors = Object.keys(this.errors).length > 0;
    const isValid = allFieldsTouched && !hasErrors;
    
    if (this.onValidate) {
      this.onValidate(isValid);
    }

    return isValid;
  }

  getData() {
    return this.data;
  }

  setData(data) {
    this.data = { ...data };
    // Convert height from feet to inches if needed
    if (this.data.height && !this.data.heightInches) {
      this.data.heightInches = Math.round(this.data.height * 12);
    }
    this.render();
    this.updateViewer();
  }
  
  destroy() {
    if (this.viewer) {
      this.viewer.destroy();
      this.viewer = null;
    }
  }
}

